import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Student,
  Teacher,
  SchoolSettings,
  RombelGuidanceNote,
  GuidanceCategory,
  GuidanceStatus,
} from '../../types.ts';
import * as XLSX from 'xlsx';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where
} from 'firebase/firestore';
import { db } from '../lib/firebase.ts';
import {
  HeartHandshake,
  Plus,
  Search,
  Filter,
  Calendar,
  User,
  Tag,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Printer,
  Trash2,
  Edit3,
  ChevronDown,
  X,
  Send,
  AlertTriangle,
  Award,
  Sparkles,
  Layers,
  PhoneCall,
  UserCheck,
  ArrowRight,
  MessageSquare,
  ShieldAlert,
  CheckSquare,
  Compass,
  Download,
  RefreshCw,
  Cloud,
  CloudOff,
  GraduationCap,
  Users,
  MapPin,
  HelpCircle,
  FileSpreadsheet
} from 'lucide-react';

export interface WaliRombelStudent {
  id: string;
  teacherId: string;
  studentId?: string;
  name: string;
  nis?: string;
  nisn?: string;
  class?: string;
  gender: 'Laki-laki' | 'Perempuan';
  notes?: string;
}

interface RombelGuidanceManagerProps {
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  waliRombelStudents: WaliRombelStudent[];
  allStudents?: Student[];
  classes?: string[];
  appTheme?: string;
}

const GUIDANCE_CATEGORIES: GuidanceCategory[] = [
  'Pribadi',
  'Sosial',
  'Belajar / Akademik',
  'Kedisiplinan & Karakter',
  'Karir & Studi Lanjut',
  'Keluarga & Ekonomi',
  'Lainnya',
];

const GUIDANCE_STATUSES: GuidanceStatus[] = [
  'Selesai',
  'Dalam Pemantauan',
  'Perlu Bimbingan Lanjutan',
  'Perlu Panggilan Orang Tua',
  'Dirujuk ke Guru BK',
];

const CATEGORY_THEMES: Record<GuidanceCategory, { bg: string; text: string; badge: string; icon: string; border: string }> = {
  Pribadi: {
    bg: 'bg-purple-500',
    text: 'text-purple-700 dark:text-purple-300',
    badge: 'bg-purple-100 dark:bg-purple-950/80 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    border: 'border-purple-300 dark:border-purple-800',
    icon: '🧘',
  },
  Sosial: {
    bg: 'bg-teal-500',
    text: 'text-teal-700 dark:text-teal-300',
    badge: 'bg-teal-100 dark:bg-teal-950/80 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800',
    border: 'border-teal-300 dark:border-teal-800',
    icon: '🤝',
  },
  'Belajar / Akademik': {
    bg: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-100 dark:bg-blue-950/80 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    border: 'border-blue-300 dark:border-blue-800',
    icon: '📚',
  },
  'Kedisiplinan & Karakter': {
    bg: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    border: 'border-amber-300 dark:border-amber-800',
    icon: '⏰',
  },
  'Karir & Studi Lanjut': {
    bg: 'bg-indigo-500',
    text: 'text-indigo-700 dark:text-indigo-300',
    badge: 'bg-indigo-100 dark:bg-indigo-950/80 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
    border: 'border-indigo-300 dark:border-indigo-800',
    icon: '🎓',
  },
  'Keluarga & Ekonomi': {
    bg: 'bg-rose-500',
    text: 'text-rose-700 dark:text-rose-300',
    badge: 'bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800',
    border: 'border-rose-300 dark:border-rose-800',
    icon: '🏡',
  },
  Lainnya: {
    bg: 'bg-slate-500',
    text: 'text-slate-700 dark:text-slate-300',
    badge: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    border: 'border-slate-300 dark:border-slate-700',
    icon: '📌',
  },
};

const STATUS_THEMES: Record<GuidanceStatus, { badge: string; icon: React.ReactNode; text: string }> = {
  Selesai: {
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800',
    icon: <CheckCircle2 size={13} className="text-emerald-600 dark:text-emerald-400" />,
    text: 'Selesai / Teratasi',
  },
  'Dalam Pemantauan': {
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950/80 dark:text-blue-300 border-blue-300 dark:border-blue-800',
    icon: <Clock size={13} className="text-blue-600 dark:text-blue-400" />,
    text: 'Dalam Pemantauan',
  },
  'Perlu Bimbingan Lanjutan': {
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border-amber-300 dark:border-amber-800',
    icon: <HelpCircle size={13} className="text-amber-600 dark:text-amber-400" />,
    text: 'Bimbingan Lanjutan',
  },
  'Perlu Panggilan Orang Tua': {
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-950/80 dark:text-orange-300 border-orange-300 dark:border-orange-800 font-bold',
    icon: <PhoneCall size={13} className="text-orange-600 dark:text-orange-400" />,
    text: 'Panggilan Orang Tua',
  },
  'Dirujuk ke Guru BK': {
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 border-purple-300 dark:border-purple-800 font-bold',
    icon: <ShieldAlert size={13} className="text-purple-600 dark:text-purple-400" />,
    text: 'Rujukan ke BK',
  },
};

const QUICK_GUIDANCE_TEMPLATES = [
  {
    topic: 'Penurunan Nilai & Kesulitan Belajar',
    category: 'Belajar / Akademik' as GuidanceCategory,
    description: 'Siswa mengalami penurunan motivasi dan nilai pada beberapa mata pelajaran, sering terlambat mengerjakan tugas.',
    guidanceGiven: 'Membimbing siswa menyusun jadwal belajar di rumah, mengidentifikasi materi yang belum dipahami, dan berkoordinasi dengan guru mata pelajaran.',
    followUp: 'Pemantauan buku tugas harian selama 2 minggu ke depan.',
    status: 'Dalam Pemantauan' as GuidanceStatus,
    location: 'Ruang Wali Kelas',
  },
  {
    topic: 'Keterlambatan & Kedisiplinan Kehadiran',
    category: 'Kedisiplinan & Karakter' as GuidanceCategory,
    description: 'Siswa tercatat datang terlambat lebih dari 3 kali dalam 2 pekan terakhir tanpa keterangan jelas.',
    guidanceGiven: 'Menggali alasan keterlambatan, memberikan pembinaan pentingnya manajemen waktu dan komitmen bangun pagi lebih awal.',
    followUp: 'Pemberian kartu kontrol presensi pagi dan koordinasi dengan orang tua.',
    status: 'Dalam Pemantauan' as GuidanceStatus,
    location: 'Pojok Bimbingan Kelas',
  },
  {
    topic: 'Konflik & Penyesuaian Sosial Pertemanan',
    category: 'Sosial' as GuidanceCategory,
    description: 'Terjadi perselisihan pendapat dengan teman sekelas yang menyebabkan siswa merasa diasingkan.',
    guidanceGiven: 'Memfasilitasi dialog mediasi kekeluargaan, membangun rasa saling menghargai dan empati antar siswa.',
    followUp: 'Pengamatan interaksi sosial siswa saat jam istirahat dan kegiatan kelompok.',
    status: 'Selesai' as GuidanceStatus,
    location: 'Ruang Wali Kelas',
  },
  {
    topic: 'Konsultasi Minat, Bakat & Rencana Studi Lanjutan',
    category: 'Karir & Studi Lanjut' as GuidanceCategory,
    description: 'Siswa membutuhkan arahan dan pertimbangan dalam memilih jurusan SMA (MIPA/IPS/Bahasa) atau SMK kejuruan.',
    guidanceGiven: 'Menganalisis nilai rapor, potensi bakat siswa, dan memberikan gambaran prospek jenjang pendidikan lanjutan.',
    followUp: 'Menyarankan diskusi lebih lanjut dengan orang tua dan guru BK.',
    status: 'Selesai' as GuidanceStatus,
    location: 'Perpustakaan Sekolah',
  },
  {
    topic: 'Kendala Keluarga / Finansial / Program KIP-PIP',
    category: 'Keluarga & Ekonomi' as GuidanceCategory,
    description: 'Siswa mengalami kendala kelengkapan alat tulis dan seragam sekolah karena kondisi ekonomi keluarga.',
    guidanceGiven: 'Mendata status bantuan sosial siswa, menghubungkan dengan program KIP/PIP sekolah dan pihak tata usaha.',
    followUp: 'Pengecekan berkas pendukung KIP/PIP ke bagian kesiswaan.',
    status: 'Dalam Pemantauan' as GuidanceStatus,
    location: 'Ruang Guru',
  },
];

export const RombelGuidanceManager: React.FC<RombelGuidanceManagerProps> = ({
  activeUser,
  schoolSettings,
  waliRombelStudents = [],
  allStudents = [],
  classes = [],
}) => {
  const storageKey = `smpn1_rombel_guidance_${activeUser.id}`;

  // Firestore Sync Status
  const [cloudSyncStatus, setCloudSyncStatus] = useState<'synced' | 'syncing' | 'offline' | 'error'>('syncing');
  const [lastSyncTime, setLastSyncTime] = useState<string>('');

  // Guidance Notes State
  const [guidanceNotes, setGuidanceNotes] = useState<RombelGuidanceNote[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.warn('Error reading cached guidance notes:', e);
    }
    return [];
  });

  // Sanitize object for Firestore
  const cleanForFirestore = useCallback((obj: any): any => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return obj.map(cleanForFirestore);
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key of Object.keys(obj)) {
        if (obj[key] !== undefined) {
          cleaned[key] = cleanForFirestore(obj[key]);
        }
      }
      return cleaned;
    }
    return obj;
  }, []);

  // REALTIME FIRESTORE LISTENER
  useEffect(() => {
    if (!db) {
      setCloudSyncStatus('offline');
      return;
    }

    setCloudSyncStatus('syncing');
    const q = query(
      collection(db, 'rombelGuidanceNotes'),
      where('teacherId', '==', activeUser.id)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items: RombelGuidanceNote[] = [];
        snapshot.forEach((d) => {
          items.push(d.data() as RombelGuidanceNote);
        });

        // Sort by date descending
        items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        setGuidanceNotes(items);
        try {
          localStorage.setItem(storageKey, JSON.stringify(items));
        } catch (e) {}

        setCloudSyncStatus('synced');
        setLastSyncTime(
          new Date().toLocaleTimeString('id-ID', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        );
      },
      (error) => {
        console.error('Firestore onSnapshot error for rombelGuidanceNotes:', error);
        setCloudSyncStatus('error');
      }
    );

    return () => unsubscribe();
  }, [activeUser.id, storageKey]);

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('ALL');
  const [selectedStudentFilter, setSelectedStudentFilter] = useState<string>('ALL');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<string>('ALL');
  const [viewLayout, setViewLayout] = useState<'cards' | 'table'>('cards');

  // Modal State for Add / Edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<RombelGuidanceNote | null>(null);

  // Form State
  const todayStr = new Date().toISOString().split('T')[0];
  const [formDate, setFormDate] = useState(todayStr);
  const [formStudentId, setFormStudentId] = useState('');
  const [formStudentName, setFormStudentName] = useState('');
  const [formStudentNis, setFormStudentNis] = useState('');
  const [formStudentClass, setFormStudentClass] = useState('');
  const [formCategory, setFormCategory] = useState<GuidanceCategory>('Belajar / Akademik');
  const [formTopic, setFormTopic] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formGuidanceGiven, setFormGuidanceGiven] = useState('');
  const [formFollowUp, setFormFollowUp] = useState('');
  const [formStatus, setFormStatus] = useState<GuidanceStatus>('Dalam Pemantauan');
  const [formResult, setFormResult] = useState('');
  const [formLocation, setFormLocation] = useState('Ruang Wali Kelas');
  const [formCounselingType, setFormCounselingType] = useState<'Individu' | 'Kelompok'>('Individu');

  // Handle Student Selection in Modal
  const handleSelectStudentInModal = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (!val) {
      setFormStudentId('');
      setFormStudentName('');
      setFormStudentNis('');
      setFormStudentClass('');
      return;
    }

    // Look strictly in wali rombel roster
    const wr = waliRombelStudents.find((s) => s.id === val || s.studentId === val);
    if (wr) {
      setFormStudentId(wr.studentId || wr.id);
      setFormStudentName(wr.name);
      setFormStudentNis(wr.nis || wr.nisn || '');
      setFormStudentClass(wr.class || '');
    }
  };

  // Open Modal to Add
  const handleOpenAddModal = (defaultStudentId?: string) => {
    setEditingNote(null);
    setFormDate(todayStr);
    setFormCategory('Belajar / Akademik');
    setFormTopic('');
    setFormDescription('');
    setFormGuidanceGiven('');
    setFormFollowUp('');
    setFormStatus('Dalam Pemantauan');
    setFormResult('');
    setFormLocation('Ruang Wali Kelas');
    setFormCounselingType('Individu');

    if (defaultStudentId) {
      const wr = waliRombelStudents.find((s) => s.id === defaultStudentId || s.studentId === defaultStudentId);
      if (wr) {
        setFormStudentId(wr.studentId || wr.id);
        setFormStudentName(wr.name);
        setFormStudentNis(wr.nis || wr.nisn || '');
        setFormStudentClass(wr.class || '');
      } else {
        setFormStudentId('');
        setFormStudentName('');
        setFormStudentNis('');
        setFormStudentClass('');
      }
    } else if (waliRombelStudents.length > 0) {
      const first = waliRombelStudents[0];
      setFormStudentId(first.studentId || first.id);
      setFormStudentName(first.name);
      setFormStudentNis(first.nis || first.nisn || '');
      setFormStudentClass(first.class || '');
    } else {
      setFormStudentId('');
      setFormStudentName('');
      setFormStudentNis('');
      setFormStudentClass('');
    }

    setIsModalOpen(true);
  };

  // Open Modal to Edit
  const handleOpenEditModal = (note: RombelGuidanceNote) => {
    setEditingNote(note);
    setFormDate(note.date);
    setFormStudentId(note.studentId || '');
    setFormStudentName(note.studentName);
    setFormStudentNis(note.studentNis || '');
    setFormStudentClass(note.studentClass || '');
    setFormCategory(note.category);
    setFormTopic(note.topic);
    setFormDescription(note.description);
    setFormGuidanceGiven(note.guidanceGiven);
    setFormFollowUp(note.followUp);
    setFormStatus(note.status);
    setFormResult(note.result || '');
    setFormLocation(note.location || 'Ruang Wali Kelas');
    setFormCounselingType(note.counselingType || 'Individu');
    setIsModalOpen(true);
  };

  // Apply Quick Template
  const handleApplyTemplate = (tpl: (typeof QUICK_GUIDANCE_TEMPLATES)[0]) => {
    setFormCategory(tpl.category);
    setFormTopic(tpl.topic);
    setFormDescription(tpl.description);
    setFormGuidanceGiven(tpl.guidanceGiven);
    setFormFollowUp(tpl.followUp);
    setFormStatus(tpl.status);
    setFormLocation(tpl.location);
  };

  // Save Note Handler
  const handleSaveNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formStudentName.trim()) {
      alert('Silakan pilih atau masukkan nama siswa yang dibimbing.');
      return;
    }
    if (!formTopic.trim() || !formDescription.trim() || !formGuidanceGiven.trim()) {
      alert('Silakan lengkapi topik, uraian permasalahan, dan arahan bimbingan.');
      return;
    }

    const noteId = editingNote ? editingNote.id : `guidance_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const newNote: RombelGuidanceNote = {
      id: noteId,
      teacherId: activeUser.id,
      teacherName: activeUser.name,
      studentId: formStudentId || undefined,
      studentName: formStudentName.trim(),
      studentNis: formStudentNis.trim() || undefined,
      studentClass: formStudentClass.trim() || undefined,
      date: formDate,
      category: formCategory,
      topic: formTopic.trim(),
      description: formDescription.trim(),
      guidanceGiven: formGuidanceGiven.trim(),
      followUp: formFollowUp.trim(),
      status: formStatus,
      result: formResult.trim() || undefined,
      location: formLocation.trim() || undefined,
      counselingType: formCounselingType,
      createdAt: editingNote?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Update local state immediately
    const updated = editingNote
      ? guidanceNotes.map((n) => (n.id === noteId ? newNote : n))
      : [newNote, ...guidanceNotes];
    updated.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    setGuidanceNotes(updated);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch (err) {}

    // Save to Firestore
    if (db) {
      try {
        setCloudSyncStatus('syncing');
        const docRef = doc(db, 'rombelGuidanceNotes', noteId);
        await setDoc(docRef, cleanForFirestore(newNote), { merge: true });
        setCloudSyncStatus('synced');
        setLastSyncTime(
          new Date().toLocaleTimeString('id-ID', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        );
      } catch (err) {
        console.error('Error saving guidance note to Firestore:', err);
        setCloudSyncStatus('error');
      }
    }

    setIsModalOpen(false);
  };

  // Delete Note Handler
  const handleDeleteNote = async (id: string) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus catatan bimbingan ini?')) return;

    const updated = guidanceNotes.filter((n) => n.id !== id);
    setGuidanceNotes(updated);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch (e) {}

    if (db) {
      try {
        setCloudSyncStatus('syncing');
        await deleteDoc(doc(db, 'rombelGuidanceNotes', id));
        setCloudSyncStatus('synced');
      } catch (err) {
        console.error('Error deleting guidance note from Firestore:', err);
        setCloudSyncStatus('error');
      }
    }
  };

  // Quick Change Status
  const handleQuickChangeStatus = async (noteId: string, newStatus: GuidanceStatus) => {
    const note = guidanceNotes.find((n) => n.id === noteId);
    if (!note) return;

    const updatedNote = { ...note, status: newStatus, updatedAt: new Date().toISOString() };
    const updated = guidanceNotes.map((n) => (n.id === noteId ? updatedNote : n));
    setGuidanceNotes(updated);
    try {
      localStorage.setItem(storageKey, JSON.stringify(updated));
    } catch (e) {}

    if (db) {
      try {
        const docRef = doc(db, 'rombelGuidanceNotes', noteId);
        await setDoc(docRef, cleanForFirestore(updatedNote), { merge: true });
      } catch (err) {
        console.error('Error quick updating status in Firestore:', err);
      }
    }
  };

  // Filtered Notes
  const filteredNotes = useMemo(() => {
    return guidanceNotes.filter((n) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        n.studentName.toLowerCase().includes(q) ||
        (n.studentNis && n.studentNis.toLowerCase().includes(q)) ||
        n.topic.toLowerCase().includes(q) ||
        n.description.toLowerCase().includes(q) ||
        n.guidanceGiven.toLowerCase().includes(q) ||
        n.followUp.toLowerCase().includes(q);

      const matchesCat = selectedCategoryFilter === 'ALL' || n.category === selectedCategoryFilter;
      const matchesStat = selectedStatusFilter === 'ALL' || n.status === selectedStatusFilter;
      const matchesStudent =
        selectedStudentFilter === 'ALL' ||
        n.studentId === selectedStudentFilter ||
        n.studentName === selectedStudentFilter;

      let matchesMonth = true;
      if (selectedMonthFilter !== 'ALL' && n.date) {
        const noteMonth = n.date.substring(0, 7); // YYYY-MM
        matchesMonth = noteMonth === selectedMonthFilter;
      }

      return matchesSearch && matchesCat && matchesStat && matchesStudent && matchesMonth;
    });
  }, [
    guidanceNotes,
    searchQuery,
    selectedCategoryFilter,
    selectedStatusFilter,
    selectedStudentFilter,
    selectedMonthFilter,
  ]);

  // Statistics
  const stats = useMemo(() => {
    const total = guidanceNotes.length;
    const selesai = guidanceNotes.filter((n) => n.status === 'Selesai').length;
    const pemantauan = guidanceNotes.filter(
      (n) => n.status === 'Dalam Pemantauan' || n.status === 'Perlu Bimbingan Lanjutan'
    ).length;
    const rujukan = guidanceNotes.filter(
      (n) => n.status === 'Dirujuk ke Guru BK' || n.status === 'Perlu Panggilan Orang Tua'
    ).length;

    // Unique students counseled
    const uniqueStudents = new Set(guidanceNotes.map((n) => n.studentName)).size;

    return { total, selesai, pemantauan, rujukan, uniqueStudents };
  }, [guidanceNotes]);

  // Months available for filter
  const availableMonths = useMemo(() => {
    const setM = new Set<string>();
    guidanceNotes.forEach((n) => {
      if (n.date) setM.add(n.date.substring(0, 7));
    });
    return Array.from(setM).sort().reverse();
  }, [guidanceNotes]);

  // PRINT REKAPITULASI JURNAL BIMBINGAN (ALL / FILTERED)
  const handlePrintRekap = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const schoolTitle = schoolSettings.schoolName || 'SMP NEGERI 1';
    const academicYear = schoolSettings.academicYear || '2025/2026';
    const semester = schoolSettings.semester || 'Ganjil';

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Jurnal Catatan Bimbingan Siswa - ${activeUser.name}</title>
          <style>
            @page { size: A4 landscape; margin: 12mm; }
            body { font-family: 'Times New Roman', Times, serif; color: #111; line-height: 1.3; margin: 0; padding: 0; font-size: 10pt; }
            .kop { text-align: center; border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 15px; }
            .kop h1 { font-size: 13pt; margin: 0; text-transform: uppercase; font-weight: bold; }
            .kop h2 { font-size: 12pt; margin: 3px 0 0 0; text-transform: uppercase; }
            .kop p { font-size: 9pt; margin: 2px 0 0 0; }
            .title { text-align: center; font-size: 11pt; font-weight: bold; text-transform: uppercase; margin-bottom: 12px; }
            .meta-table { width: 100%; margin-bottom: 12px; font-size: 9.5pt; }
            .meta-table td { padding: 2px 4px; vertical-align: top; border: none; }
            table.data-table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 9pt; }
            table.data-table th, table.data-table td { border: 1px solid #000; padding: 5px 6px; text-align: left; vertical-align: top; }
            table.data-table th { background-color: #f2f2f2; text-align: center; font-weight: bold; }
            .text-center { text-align: center; }
            .status-tag { font-weight: bold; font-size: 8.5pt; }
            .sig-table { width: 100%; margin-top: 25px; page-break-inside: avoid; }
            .sig-table td { border: none; text-align: center; vertical-align: top; font-size: 9.5pt; }
            .sig-space { height: 50px; }
          </style>
        </head>
        <body>
          <div class="kop">
            <h1>${(schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS').toUpperCase()}</h1>
            <h2>${(schoolSettings.departmentName || 'DINAS PENDIDIKAN').toUpperCase()}</h2>
            <h1>${schoolTitle.toUpperCase()}</h1>
            <p>${schoolSettings.address || 'Jl. Raya Pendidikan No. 1'}</p>
          </div>

          <div class="title">
            JURNAL & BUKU CATATAN BIMBINGAN SISWA PERWALIAN (WALI ROMBEL)
          </div>

          <table class="meta-table">
            <tr>
              <td width="18%"><strong>Nama Guru / Wali Rombel</strong></td>
              <td width="3%">:</td>
              <td width="40%">${activeUser.name} (NIP. ${activeUser.nip || '-'})</td>
              <td width="15%"><strong>Tahun Ajaran</strong></td>
              <td width="3%">:</td>
              <td width="21%">${academicYear}</td>
            </tr>
            <tr>
              <td><strong>Mata Pelajaran / Tugas</strong></td>
              <td>:</td>
              <td>${activeUser.subject || 'Wali Rombel'}</td>
              <td><strong>Semester</strong></td>
              <td>:</td>
              <td>${semester}</td>
            </tr>
            <tr>
              <td><strong>Jumlah Catatan Sesi</strong></td>
              <td>:</td>
              <td>${filteredNotes.length} Sesi Bimbingan</td>
              <td><strong>Tanggal Cetak</strong></td>
              <td>:</td>
              <td>${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</td>
            </tr>
          </table>

          <table class="data-table">
            <thead>
              <tr>
                <th width="4%">No</th>
                <th width="10%">Tanggal</th>
                <th width="15%">Nama Siswa & Kelas</th>
                <th width="12%">Bidang Bimbingan</th>
                <th width="18%">Pokok Masalah / Topik</th>
                <th width="20%">Arahan / Bimbingan Wali Rombel</th>
                <th width="12%">Rencana Tindak Lanjut</th>
                <th width="9%">Status</th>
              </tr>
            </thead>
            <tbody>
              ${
                filteredNotes.length === 0
                  ? '<tr><td colspan="8" class="text-center" style="padding: 20px;">Belum ada catatan bimbingan yang terdata.</td></tr>'
                  : filteredNotes
                      .map(
                        (n, idx) => `
                <tr>
                  <td class="text-center">${idx + 1}</td>
                  <td class="text-center">${new Date(n.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>
                    <strong>${n.studentName}</strong><br/>
                    <small>NIS: ${n.studentNis || '-'} • Kelas: ${n.studentClass || '-'}</small>
                  </td>
                  <td>
                    <strong>${n.category}</strong><br/>
                    <small>${n.counselingType || 'Individu'} • ${n.location || '-'}</small>
                  </td>
                  <td>
                    <strong>${n.topic}</strong><br/>
                    <small>${n.description}</small>
                  </td>
                  <td>${n.guidanceGiven}</td>
                  <td>${n.followUp || '-'}</td>
                  <td class="text-center status-tag">${n.status}</td>
                </tr>
              `
                      )
                      .join('')
              }
            </tbody>
          </table>

          <table class="sig-table">
            <tr>
              <td width="50%">
                Mengetahui,<br/>
                Kepala Sekolah ${schoolTitle}
                <div class="sig-space"></div>
                <strong>${schoolSettings.principalName || '................................'}</strong><br/>
                NIP. ${schoolSettings.principalNip || '................................'}
              </td>
              <td width="50%">
                ${schoolSettings.address ? schoolSettings.address.split(',')[0] : 'Ciamis'}, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Guru Pembimbing / Wali Rombel
                <div class="sig-space"></div>
                <strong>${activeUser.name}</strong><br/>
                NIP. ${activeUser.nip || '................................'}
              </td>
            </tr>
          </table>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;

    printWin.document.write(content);
    printWin.document.close();
  };

  // PRINT LEMBAR KARTU BIMBINGAN INDIVIDU
  const handlePrintIndividualCard = (note: RombelGuidanceNote) => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const schoolTitle = schoolSettings.schoolName || 'SMP NEGERI 1';

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kartu Rekam Bimbingan Siswa - ${note.studentName}</title>
          <style>
            @page { size: A4 portrait; margin: 15mm; }
            body { font-family: 'Times New Roman', Times, serif; color: #111; line-height: 1.4; margin: 0; padding: 0; font-size: 11pt; }
            .kop { text-align: center; border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 20px; }
            .kop h1 { font-size: 14pt; margin: 0; text-transform: uppercase; font-weight: bold; }
            .kop h2 { font-size: 12pt; margin: 3px 0 0 0; text-transform: uppercase; }
            .kop p { font-size: 9.5pt; margin: 2px 0 0 0; }
            .title { text-align: center; font-size: 12pt; font-weight: bold; text-transform: uppercase; margin-bottom: 20px; text-decoration: underline; }
            .info-box { border: 1px solid #000; padding: 12px; margin-bottom: 15px; background: #fafafa; border-radius: 4px; }
            table.form-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 10.5pt; }
            table.form-table td { padding: 6px 4px; vertical-align: top; border-bottom: 1px dashed #ddd; }
            .section-header { font-weight: bold; font-size: 11pt; margin-top: 15px; margin-bottom: 6px; text-transform: uppercase; background: #f0f0f0; padding: 4px 8px; border-left: 4px solid #333; }
            .content-box { border: 1px solid #999; padding: 10px; min-height: 50px; font-size: 10.5pt; margin-bottom: 10px; background: #fff; line-height: 1.5; white-space: pre-wrap; }
            .sig-table { width: 100%; margin-top: 40px; page-break-inside: avoid; }
            .sig-table td { border: none; text-align: center; vertical-align: top; font-size: 10pt; }
            .sig-space { height: 60px; }
          </style>
        </head>
        <body>
          <div class="kop">
            <h1>${(schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS').toUpperCase()}</h1>
            <h2>${(schoolSettings.departmentName || 'DINAS PENDIDIKAN').toUpperCase()}</h2>
            <h1>${schoolTitle.toUpperCase()}</h1>
            <p>${schoolSettings.address || 'Jl. Raya Pendidikan No. 1'}</p>
          </div>

          <div class="title">
            LEMBAR REKAM BIMBINGAN & KONSELING SISWA
          </div>

          <table class="form-table">
            <tr>
              <td width="28%"><strong>Nama Peserta Didik</strong></td>
              <td width="3%">:</td>
              <td width="69%"><strong>${note.studentName}</strong></td>
            </tr>
            <tr>
              <td><strong>NIS / NISN</strong></td>
              <td>:</td>
              <td>${note.studentNis || '-'}</td>
            </tr>
            <tr>
              <td><strong>Kelas / Rombel</strong></td>
              <td>:</td>
              <td>${note.studentClass || '-'}</td>
            </tr>
            <tr>
              <td><strong>Hari / Tanggal Bimbingan</strong></td>
              <td>:</td>
              <td>${new Date(note.date).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</td>
            </tr>
            <tr>
              <td><strong>Bidang Bimbingan</strong></td>
              <td>:</td>
              <td>${note.category} (${note.counselingType || 'Individu'})</td>
            </tr>
            <tr>
              <td><strong>Tempat Pelaksanaan</strong></td>
              <td>:</td>
              <td>${note.location || 'Ruang Wali Kelas'}</td>
            </tr>
            <tr>
              <td><strong>Guru Pembimbing / Walas</strong></td>
              <td>:</td>
              <td>${note.teacherName || activeUser.name}</td>
            </tr>
            <tr>
              <td><strong>Status Akhir</strong></td>
              <td>:</td>
              <td><strong>${note.status}</strong></td>
            </tr>
          </table>

          <div class="section-header">1. Pokok Masalah / Topik Bimbingan</div>
          <div class="content-box"><strong>${note.topic}</strong>\n${note.description}</div>

          <div class="section-header">2. Arahan / Solusi / Pendekatan Bimbingan</div>
          <div class="content-box">${note.guidanceGiven}</div>

          <div class="section-header">3. Rencana Tindak Lanjut</div>
          <div class="content-box">${note.followUp || 'Pemantauan berkala dan koordinasi lanjutan.'}</div>

          ${
            note.result
              ? `
            <div class="section-header">4. Catatan Evaluasi & Perkembangan Siswa</div>
            <div class="content-box">${note.result}</div>
          `
              : ''
          }

          <table class="sig-table">
            <tr>
              <td width="50%">
                Peserta Didik yang Dibimbing,
                <div class="sig-space"></div>
                <strong>${note.studentName}</strong><br/>
                NIS. ${note.studentNis || '......................'}
              </td>
              <td width="50%">
                ${schoolSettings.address ? schoolSettings.address.split(',')[0] : 'Ciamis'}, ${new Date(note.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Guru Pembimbing / Wali Rombel
                <div class="sig-space"></div>
                <strong>${note.teacherName || activeUser.name}</strong><br/>
                NIP. ${activeUser.nip || '................................'}
              </td>
            </tr>
          </table>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;

    printWin.document.write(content);
    printWin.document.close();
  };

  // EXPORT EXCEL
  const handleExportExcel = () => {
    const schoolTitle = schoolSettings.schoolName || 'SMP NEGERI 1';

    const headerData = [
      [schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'],
      [schoolSettings.departmentName || 'DINAS PENDIDIKAN'],
      [schoolTitle.toUpperCase()],
      [schoolSettings.address || ''],
      [],
      ['JURNAL CATATAN BIMBINGAN SISWA PERWALIAN (WALI ROMBEL)'],
      [`Wali Rombel: ${activeUser.name} (NIP. ${activeUser.nip || '-'})`],
      [`Tahun Ajaran: ${schoolSettings.academicYear || '2025/2026'} - Semester: ${schoolSettings.semester || 'Ganjil'}`],
      [],
      [
        'No',
        'Tanggal',
        'Nama Siswa',
        'NIS',
        'Kelas',
        'Bidang Bimbingan',
        'Bentuk',
        'Tempat',
        'Topik Masalah',
        'Uraian Masalah',
        'Arahan / Bimbingan Diberikan',
        'Rencana Tindak Lanjut',
        'Status',
        'Hasil Perkembangan',
      ],
    ];

    const dataRows = filteredNotes.map((n, idx) => [
      idx + 1,
      n.date,
      n.studentName,
      n.studentNis || '',
      n.studentClass || '',
      n.category,
      n.counselingType || 'Individu',
      n.location || '',
      n.topic,
      n.description,
      n.guidanceGiven,
      n.followUp,
      n.status,
      n.result || '',
    ]);

    const ws = XLSX.utils.aoa_to_sheet([...headerData, ...dataRows]);

    ws['!cols'] = [
      { wch: 5 },
      { wch: 12 },
      { wch: 25 },
      { wch: 15 },
      { wch: 10 },
      { wch: 22 },
      { wch: 12 },
      { wch: 18 },
      { wch: 30 },
      { wch: 40 },
      { wch: 40 },
      { wch: 30 },
      { wch: 22 },
      { wch: 30 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Catatan_Bimbingan');
    XLSX.writeFile(
      wb,
      `Catatan_Bimbingan_Rombel_${activeUser.name.replace(/\s+/g, '_')}_${todayStr}.xlsx`
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Firebase Status */}
      <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-6 sm:p-8 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white flex items-center justify-center shadow-lg shadow-purple-500/20 shrink-0">
            <HeartHandshake size={28} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl sm:text-2xl font-black text-slate-800 dark:text-white leading-tight">
                Catatan Bimbingan Siswa
              </h2>
              {/* Cloud sync indicator */}
              <div
                className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                  cloudSyncStatus === 'synced'
                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                    : cloudSyncStatus === 'syncing'
                    ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                    : 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                }`}
                title="Status Sinkronisasi Firebase Cloud Firestore"
              >
                {cloudSyncStatus === 'synced' ? (
                  <>
                    <Cloud size={12} className="text-emerald-600 dark:text-emerald-400" />
                    <span>Cloud Firestore Synced</span>
                    {lastSyncTime && <span className="opacity-70">({lastSyncTime})</span>}
                  </>
                ) : cloudSyncStatus === 'syncing' ? (
                  <>
                    <RefreshCw size={12} className="animate-spin text-amber-600" />
                    <span>Menyimpan ke Cloud...</span>
                  </>
                ) : (
                  <>
                    <CloudOff size={12} className="text-rose-600" />
                    <span>Offline / Local Mode</span>
                  </>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold mt-1">
              Pencatatan bimbingan pribadi, sosial, akademik, kedisiplinan, dan konseling siswa perwalian Wali Rombel.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          <button
            onClick={() => handleOpenAddModal()}
            className="w-full md:w-auto px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg shadow-indigo-500/25 flex items-center justify-center gap-2 transition-all transform hover:scale-102 cursor-pointer"
          >
            <Plus size={16} />
            <span>Catat Bimbingan Baru</span>
          </button>
        </div>
      </div>

      {/* Summary Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">TOTAL BIMBINGAN</span>
            <div className="p-2 rounded-xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400">
              <HeartHandshake size={16} />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-800 dark:text-white">{stats.total}</div>
          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">
            {stats.uniqueStudents} Siswa Dibimbing
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">SELESAI / TUNTAS</span>
            <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">{stats.selesai}</div>
          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Kasus terselesaikan</p>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">DALAM PEMANTAUAN</span>
            <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400">
              <Clock size={16} />
            </div>
          </div>
          <div className="text-2xl font-black text-blue-600 dark:text-blue-400">{stats.pemantauan}</div>
          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Perlu tindak lanjut</p>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">RUJUKAN BK / ORTU</span>
            <div className="p-2 rounded-xl bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400">
              <ShieldAlert size={16} />
            </div>
          </div>
          <div className="text-2xl font-black text-purple-600 dark:text-purple-400">{stats.rujukan}</div>
          <p className="text-[11px] text-slate-400 font-semibold mt-0.5">Perlu kolaborasi khusus</p>
        </div>
      </div>

      {/* Filter and Action Bar */}
      <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-5 shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center justify-between">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari nama siswa, NIS, topik, kata kunci bimbingan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl outline-none font-bold text-xs text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 transition-all"
            />
          </div>

          {/* Quick Filter Selectors */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Student Filter */}
            <select
              value={selectedStudentFilter}
              onChange={(e) => setSelectedStudentFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-700 dark:text-slate-300 outline-none"
            >
              <option value="ALL">Semua Siswa</option>
              {waliRombelStudents.map((s) => (
                <option key={s.id} value={s.studentId || s.id}>
                  {s.name} ({s.class || 'Rombel'})
                </option>
              ))}
            </select>

            {/* Category Filter */}
            <select
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-700 dark:text-slate-300 outline-none"
            >
              <option value="ALL">Semua Bidang</option>
              {GUIDANCE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value)}
              className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-700 dark:text-slate-300 outline-none"
            >
              <option value="ALL">Semua Status</option>
              {GUIDANCE_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>

            {/* Month Filter */}
            {availableMonths.length > 0 && (
              <select
                value={selectedMonthFilter}
                onChange={(e) => setSelectedMonthFilter(e.target.value)}
                className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-700 dark:text-slate-300 outline-none"
              >
                <option value="ALL">Semua Bulan</option>
                {availableMonths.map((m) => {
                  const [yr, mo] = m.split('-');
                  const monthName = new Date(parseInt(yr), parseInt(mo) - 1, 1).toLocaleDateString('id-ID', {
                    month: 'long',
                    year: 'numeric',
                  });
                  return (
                    <option key={m} value={m}>
                      {monthName}
                    </option>
                  );
                })}
              </select>
            )}

            {/* Layout Switcher */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setViewLayout('cards')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                  viewLayout === 'cards'
                    ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
                title="Tampilan Kartu Kronologis"
              >
                <Layers size={14} className="inline mr-1" />
                Kartu
              </button>
              <button
                type="button"
                onClick={() => setViewLayout('table')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                  viewLayout === 'table'
                    ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-300 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
                title="Tampilan Tabel Jurnal"
              >
                <FileSpreadsheet size={14} className="inline mr-1" />
                Tabel
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons: Print & Excel */}
        <div className="flex flex-wrap items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
          <span className="text-slate-400 font-semibold">
            Menampilkan <strong className="text-slate-700 dark:text-slate-200">{filteredNotes.length}</strong> dari{' '}
            {guidanceNotes.length} catatan bimbingan
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrintRekap}
              disabled={filteredNotes.length === 0}
              className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold rounded-xl flex items-center gap-1.5 transition-all disabled:opacity-50 cursor-pointer"
            >
              <Printer size={14} />
              <span>Cetak Jurnal (PDF)</span>
            </button>

            <button
              onClick={handleExportExcel}
              disabled={filteredNotes.length === 0}
              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center gap-1.5 transition-all disabled:opacity-50 cursor-pointer shadow-xs"
            >
              <Download size={14} />
              <span>Export Excel</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Guidance List / Cards */}
      {filteredNotes.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-purple-50 dark:bg-purple-950/40 text-purple-500 flex items-center justify-center mx-auto mb-4 border border-purple-100 dark:border-purple-900/50">
            <HeartHandshake size={32} />
          </div>
          <h3 className="text-lg font-black text-slate-800 dark:text-white mb-1">
            Belum Ada Catatan Bimbingan
          </h3>
          <p className="text-xs text-slate-400 font-semibold max-w-md mx-auto leading-relaxed">
            {searchQuery || selectedCategoryFilter !== 'ALL' || selectedStatusFilter !== 'ALL'
              ? 'Tidak ada catatan bimbingan yang sesuai dengan kriteria pencarian / filter Anda.'
              : 'Anda belum mencatat sesi bimbingan untuk siswa rombel. Klik tombol di bawah untuk menambahkan catatan bimbingan konseling pertama.'}
          </p>
          <button
            onClick={() => handleOpenAddModal()}
            className="mt-6 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-purple-500/20 inline-flex items-center gap-2 cursor-pointer"
          >
            <Plus size={16} />
            <span>Catat Bimbingan Sekarang</span>
          </button>
        </div>
      ) : viewLayout === 'cards' ? (
        /* CARDS TIMELINE VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredNotes.map((note) => {
            const catTheme = CATEGORY_THEMES[note.category] || CATEGORY_THEMES['Lainnya'];
            const statusTheme = STATUS_THEMES[note.status] || STATUS_THEMES['Dalam Pemantauan'];

            return (
              <div
                key={note.id}
                className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 p-5 sm:p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group relative overflow-hidden"
              >
                {/* Top Accent Strip */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${catTheme.bg}`} />

                <div>
                  {/* Top Bar: Date, Category Badge & Status Badge */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider border ${catTheme.badge}`}
                      >
                        <span>{catTheme.icon}</span>
                        <span>{note.category}</span>
                      </span>

                      <span className="text-[11px] font-bold text-slate-400 flex items-center gap-1 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded-lg border border-slate-100 dark:border-slate-700/60">
                        <Calendar size={11} />
                        {new Date(note.date).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </div>

                    {/* Status Dropdown / Badge */}
                    <div className="relative shrink-0">
                      <select
                        value={note.status}
                        onChange={(e) => handleQuickChangeStatus(note.id, e.target.value as GuidanceStatus)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider border outline-none cursor-pointer ${statusTheme.badge}`}
                        title="Klik untuk ubah status bimbingan"
                      >
                        {GUIDANCE_STATUSES.map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Student Name & Meta */}
                  <div className="flex items-center gap-3 p-2.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 flex items-center justify-center font-black text-sm shrink-0 shadow-xs">
                      {note.studentName.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-black text-sm text-slate-900 dark:text-white truncate">
                        {note.studentName}
                      </h4>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold flex items-center gap-2">
                        {note.studentNis && <span>NIS: {note.studentNis}</span>}
                        {note.studentClass && <span>Kelas {note.studentClass}</span>}
                        {note.location && <span>• {note.location}</span>}
                      </p>
                    </div>
                  </div>

                  {/* Topic / Problem */}
                  <div className="mb-3">
                    <h5 className="font-black text-sm text-slate-800 dark:text-slate-100 mb-1 flex items-center gap-1.5">
                      <MessageSquare size={14} className="text-purple-500 shrink-0" />
                      <span>{note.topic}</span>
                    </h5>
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed bg-slate-50/70 dark:bg-slate-900/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800/80">
                      {note.description}
                    </p>
                  </div>

                  {/* Guidance Given / Action Taken */}
                  <div className="mb-3 space-y-2">
                    <div className="bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 p-3 rounded-xl">
                      <span className="text-[10px] font-black text-indigo-700 dark:text-indigo-300 uppercase tracking-wider block mb-1 flex items-center gap-1">
                        <HeartHandshake size={12} />
                        <span>Arahan / Solusi Wali Rombel:</span>
                      </span>
                      <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed font-medium">
                        {note.guidanceGiven}
                      </p>
                    </div>

                    {note.followUp && (
                      <div className="bg-amber-50/60 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/40 p-2.5 rounded-xl">
                        <span className="text-[10px] font-black text-amber-800 dark:text-amber-300 uppercase tracking-wider block mb-0.5 flex items-center gap-1">
                          <Clock size={11} />
                          <span>Rencana Tindak Lanjut:</span>
                        </span>
                        <p className="text-[11px] text-slate-700 dark:text-slate-300">{note.followUp}</p>
                      </div>
                    )}

                    {note.result && (
                      <div className="bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 p-2.5 rounded-xl">
                        <span className="text-[10px] font-black text-emerald-800 dark:text-emerald-300 uppercase tracking-wider block mb-0.5 flex items-center gap-1">
                          <CheckCircle2 size={11} />
                          <span>Evaluasi & Perkembangan:</span>
                        </span>
                        <p className="text-[11px] text-slate-700 dark:text-slate-300">{note.result}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Card Footer Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800 mt-2">
                  <span className="text-[10px] text-slate-400 font-bold">
                    Pembimbing: {note.teacherName || activeUser.name}
                  </span>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handlePrintIndividualCard(note)}
                      className="p-1.5 text-slate-500 hover:text-purple-600 bg-slate-100 dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/50 rounded-lg transition-colors cursor-pointer"
                      title="Cetak Lembar Bimbingan Siswa (Individual)"
                    >
                      <Printer size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleOpenEditModal(note)}
                      className="p-1.5 text-slate-500 hover:text-indigo-600 bg-slate-100 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-lg transition-colors cursor-pointer"
                      title="Edit Catatan Bimbingan"
                    >
                      <Edit3 size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeleteNote(note.id)}
                      className="p-1.5 text-slate-400 hover:text-rose-600 bg-slate-100 dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-lg transition-colors cursor-pointer"
                      title="Hapus Catatan Bimbingan"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* TABLE VIEW */
        <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                  <th className="py-3 px-3 text-center w-10">No</th>
                  <th className="py-3 px-3 w-28">Tanggal</th>
                  <th className="py-3 px-4">Nama Siswa</th>
                  <th className="py-3 px-3">Bidang</th>
                  <th className="py-3 px-4">Topik & Masalah</th>
                  <th className="py-3 px-4">Arahan & Solusi</th>
                  <th className="py-3 px-3 text-center">Status</th>
                  <th className="py-3 px-3 text-center w-24">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredNotes.map((note, idx) => {
                  const catTheme = CATEGORY_THEMES[note.category] || CATEGORY_THEMES['Lainnya'];
                  const statusTheme = STATUS_THEMES[note.status] || STATUS_THEMES['Dalam Pemantauan'];

                  return (
                    <tr
                      key={note.id}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-3 px-3 text-center font-bold text-slate-400">{idx + 1}</td>
                      <td className="py-3 px-3 font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                        {new Date(note.date).toLocaleDateString('id-ID', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-bold text-slate-900 dark:text-white block">
                          {note.studentName}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          NIS: {note.studentNis || '-'} • Kls: {note.studentClass || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${catTheme.badge}`}
                        >
                          <span>{catTheme.icon}</span>
                          <span>{note.category}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4 max-w-xs">
                        <strong className="text-slate-800 dark:text-slate-200 block truncate">
                          {note.topic}
                        </strong>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">
                          {note.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 max-w-xs">
                        <p className="text-[11px] text-slate-700 dark:text-slate-300 line-clamp-2">
                          {note.guidanceGiven}
                        </p>
                        {note.followUp && (
                          <span className="text-[10px] text-amber-700 dark:text-amber-300 font-semibold block truncate">
                            TL: {note.followUp}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-center whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${statusTheme.badge}`}
                        >
                          {statusTheme.text}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => handlePrintIndividualCard(note)}
                            className="p-1.5 text-slate-400 hover:text-purple-600 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-950/50 transition-colors cursor-pointer"
                            title="Cetak Lembar Bimbingan"
                          >
                            <Printer size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(note)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <Edit3 size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteNote(note.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors cursor-pointer"
                            title="Hapus"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL TAMBAH / EDIT CATATAN BIMBINGAN */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden my-8 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white relative shrink-0">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="absolute right-4 top-4 p-2 text-white/80 hover:text-white rounded-full bg-black/20 hover:bg-black/40 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <HeartHandshake size={22} />
                </div>
                <div>
                  <h3 className="font-black text-lg text-white">
                    {editingNote ? 'Edit Catatan Bimbingan' : 'Tambah Catatan Bimbingan Siswa'}
                  </h3>
                  <p className="text-xs text-white/80 font-medium">
                    Wali Rombel: {activeUser.name}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Body Form */}
            <form onSubmit={handleSaveNote} className="p-6 overflow-y-auto space-y-4 flex-1 custom-scrollbar">
              {/* Quick Template Chips */}
              {!editingNote && (
                <div>
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5 flex items-center gap-1">
                    <Sparkles size={12} className="text-amber-500" />
                    <span>Template Bimbingan Cepat:</span>
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {QUICK_GUIDANCE_TEMPLATES.map((tpl, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleApplyTemplate(tpl)}
                        className="px-2.5 py-1 text-[11px] font-bold bg-slate-100 dark:bg-slate-800 hover:bg-purple-100 hover:text-purple-800 dark:hover:bg-purple-950 dark:hover:text-purple-300 text-slate-700 dark:text-slate-300 rounded-lg transition-colors border border-slate-200 dark:border-slate-700 cursor-pointer"
                      >
                        {tpl.topic}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Student Picker & Date */}
              {waliRombelStudents.length === 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 rounded-xl text-amber-800 dark:text-amber-300 text-xs font-bold flex items-center gap-2">
                  <AlertCircle size={16} className="text-amber-600 shrink-0" />
                  <span>Daftar roster siswa rombel Anda masih kosong. Silakan tambahkan siswa ke roster rombel terlebih dahulu di tab <strong>Daftar Roster Siswa</strong>.</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Pilih Siswa (Dari Daftar Roster Rombel) <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formStudentId}
                    onChange={handleSelectStudentInModal}
                    required
                    disabled={waliRombelStudents.length === 0}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60"
                  >
                    {waliRombelStudents.length === 0 ? (
                      <option value="" disabled>-- Tidak ada siswa di roster rombel --</option>
                    ) : (
                      <>
                        <option value="" disabled>-- Pilih Siswa Roster Rombel --</option>
                        {waliRombelStudents.map((s) => (
                          <option key={s.id} value={s.studentId || s.id}>
                            {s.name} ({s.class ? `Kelas ${s.class}` : 'Rombel'}{s.gender ? ` - ${s.gender}` : ''})
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {formStudentName && (
                    <div className="mt-1.5 text-[11px] text-purple-700 dark:text-purple-300 font-bold flex items-center gap-2 px-2 py-1 bg-purple-50 dark:bg-purple-950/40 rounded-lg border border-purple-100 dark:border-purple-900/40">
                      <span>Terpilih: <strong>{formStudentName}</strong></span>
                      {formStudentClass && <span>• Kelas {formStudentClass}</span>}
                      {formStudentNis && <span>• NIS: {formStudentNis}</span>}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Tanggal Bimbingan <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Category, Counseling Type & Location */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Bidang Bimbingan <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as GuidanceCategory)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {GUIDANCE_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Bentuk Bimbingan
                  </label>
                  <select
                    value={formCounselingType}
                    onChange={(e) => setFormCounselingType(e.target.value as 'Individu' | 'Kelompok')}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="Individu">Individu (Perorangan)</option>
                    <option value="Kelompok">Kelompok (Group)</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Tempat / Lokasi
                  </label>
                  <input
                    type="text"
                    placeholder="Ruang Wali Kelas / BK / dll"
                    value={formLocation}
                    onChange={(e) => setFormLocation(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Topic / Title */}
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Pokok Bahasan / Topik Masalah <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Contoh: Kesulitan memahami materi Matematika & tugas tertunda"
                  value={formTopic}
                  onChange={(e) => setFormTopic(e.target.value)}
                  required
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Description / Problem */}
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Uraian Masalah / Gejala yang Teramati <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="Jelaskan detail situasi, latar belakang masalah, perilaku siswa yang teramati..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  required
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Guidance Given / Advice */}
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Arahan / Bimbingan / Solusi yang Diberikan <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="Uraikan pendekatan konseling, nasihat, kesepakatan bersama siswa, atau solusi alternatif yang disepakati..."
                  value={formGuidanceGiven}
                  onChange={(e) => setFormGuidanceGiven(e.target.value)}
                  required
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Follow Up & Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Rencana Tindak Lanjut
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: Pemantauan tugas selama 1 minggu, koordinasi ortu..."
                    value={formFollowUp}
                    onChange={(e) => setFormFollowUp(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                    Status Bimbingan <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as GuidanceStatus)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {GUIDANCE_STATUSES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Evaluation Result Note */}
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Catatan Evaluasi / Hasil Perkembangan Siswa (Opsional)
                </label>
                <input
                  type="text"
                  placeholder="Contoh: Siswa sudah mulai aktif mengumpulkan tugas dan bersemangat..."
                  value={formResult}
                  onChange={(e) => setFormResult(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-purple-500/20 transition-all cursor-pointer"
                >
                  {editingNote ? 'Simpan Perubahan' : 'Simpan Catatan Bimbingan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
